/**
 * What: The fail-loud default props the Study Builder block renderer falls back to when a caller renders blocks but supplies
 *   no client handlers or components (a Ladle story). Split out of `SectionBlocks` only to keep that file under its line cap.
 * Transport / data-flow: Plain constants; the types they are shaped by live in `SectionBlocks` and are imported for typing only.
 */
import type { ClientActions, ClientComponents, Locks, PendingValues } from './SectionBlocks'

export const NO_PENDING: PendingValues = new Map()
export const NO_LOCKS: Locks = { text: false, structure: false }

/**
 * The default when a caller renders blocks but supplies no client handlers (a Ladle story). It does not silently no-op: a
 * block that names a client action and is actually pressed throws, so a missing handler fails loud rather than doing nothing.
 * The real builder always passes live handlers, so this is only ever reached from a preview.
 */
export const FAIL_WITHOUT_CLIENT_ACTIONS: ClientActions = {
  'choose-product': () => {
    throw new Error('STUDY_BUILDER_MISSING_CLIENT_ACTION')
  },
}
function failMissingComponent(): never {
  throw new Error('STUDY_BUILDER_MISSING_CLIENT_COMPONENT')
}
/**
 * The default when a caller renders blocks but registers no client components. It does not silently render nothing: a `client`
 * block drawn without its component throws, so a missing registration fails loud. Only the summary sidebar sends `client`
 * blocks, and its renderer always passes the live registry, so this is never reached in the editable sections.
 */
export const FAIL_WITHOUT_CLIENT_COMPONENTS: ClientComponents = {
  'summary-product': failMissingComponent,
  'summary-testing': failMissingComponent,
  'summary-audience': failMissingComponent,
  'summary-shoppers': failMissingComponent,
}
